import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { WifiOff, RefreshCw, CloudUpload, X, AlertTriangle, Printer, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { getQueue, updateItem, removeItem, counts } from "./offlineQueue";
import { printThermal, rd } from "./posShared";

export default function OfflineSync({ products = [], settings, onSynced }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [c, setC] = useState(counts());
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(() => setC(counts()), []);

  const syncAll = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    const pend = getQueue().filter((i) => i.status === "pending");
    if (!pend.length) return;
    setSyncing(true);
    let ok = 0, err = 0;
    for (const item of pend) {
      try {
        await api.post("/pos/invoices", item.body);
        removeItem(item.qid);
        ok++;
      } catch (e) {
        if (!e.response) break; // network dropped again -> stop, keep pending
        updateItem(item.qid, { status: "error", error: formatApiErrorDetail(e.response?.data?.detail) });
        err++;
      }
    }
    setSyncing(false);
    refresh();
    if (ok) { toast.success(`${ok} factura(s) sincronizada(s)`); onSynced && onSynced(); }
    if (err) toast.error(`${err} factura(s) con error. Revisa "Pendientes de sincronizar".`);
  }, [syncing, refresh, onSynced]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncAll(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    window.addEventListener("mbv-queue-changed", refresh);
    // try syncing on mount if we start online with a backlog
    if (navigator.onLine) syncAll();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("mbv-queue-changed", refresh);
    };
  }, [syncAll, refresh]);

  const pname = (pid) => products.find((p) => p.id === pid)?.name || "Producto";

  const reprint = (item) => {
    const b = item.body;
    const items = (b.items || []).map((it) => ({ name: pname(it.product_id), qty: it.qty, price: it.price }));
    const subtotal = items.reduce((s, it) => s + it.price * it.qty, 0);
    const disc = Number(b.discount) || 0;
    const base = Math.max(0, subtotal - disc);
    const rate = Number(settings?.itbis_rate ?? 18);
    const itbis = b.apply_itbis ? Math.round(base * rate) / 100 : 0;
    printThermal({
      number: item.number, _provisional: true, status: "pendiente_sync",
      customer_name: item.customer_name || "Cliente General", items,
      subtotal, discount: disc, itbis, itbis_rate: rate, total: round2(base + itbis),
      payment_method: b.payment_method, created_at: item.created_at, seller: item.seller || "",
    }, settings || {});
  };

  const retry = (item) => { updateItem(item.qid, { status: "pending", error: "" }); setTimeout(syncAll, 50); };
  const discard = (item) => { if (window.confirm(`¿Descartar ${item.number}? No se registrará esta venta.`)) removeItem(item.qid); };

  const badge = c.total > 0;
  if (!badge && online) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 z-[110] flex flex-col items-start gap-2" data-testid="offline-sync">
        {!online && (
          <div className="flex items-center gap-2 bg-amber-500 text-white rounded-full px-4 py-2 text-xs font-medium shadow-lg">
            <WifiOff size={14} /> Sin conexión · facturando en modo offline
          </div>
        )}
        {badge && (
          <button data-testid="offline-badge" onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-espresso text-cream rounded-full px-4 py-2 text-xs font-medium shadow-lg hover:bg-rose transition">
            <CloudUpload size={14} />
            {c.pending > 0 ? `${c.pending} por sincronizar` : ""}{c.pending > 0 && c.error > 0 ? " · " : ""}
            {c.error > 0 ? `${c.error} con error` : ""}
            {online && c.pending > 0 && <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />}
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[115] bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 sticky top-0 bg-white">
              <span className="font-medium">Pendientes de sincronizar</span>
              <div className="flex items-center gap-2">
                <button data-testid="offline-sync-now" onClick={syncAll} disabled={!online || syncing}
                  className="inline-flex items-center gap-1.5 text-xs bg-espresso text-cream rounded-full px-3 py-1.5 hover:bg-rose transition disabled:opacity-40">
                  <RefreshCw size={13} className={syncing ? "animate-spin" : ""} /> Sincronizar
                </button>
                <button onClick={() => setOpen(false)} className="text-neutral-400 hover:text-espresso"><X size={20} /></button>
              </div>
            </div>
            <div className="p-5 space-y-2">
              {!online && <p className="text-xs text-amber-600 flex items-center gap-1.5"><WifiOff size={13} /> Estás sin conexión. Se sincronizará automáticamente al reconectar.</p>}
              {getQueue().length === 0 && <p className="text-neutral-400 text-sm py-6 text-center">No hay facturas pendientes. Todo sincronizado.</p>}
              {getQueue().map((item) => (
                <div key={item.qid} data-testid={`offline-item-${item.number}`} className="border border-neutral-100 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{item.number} <span className="text-neutral-400 font-normal">· {item.customer_name || "Cliente General"}</span></div>
                      <div className="text-xs text-neutral-400">{(item.created_at || "").slice(0, 16).replace("T", " ")} · {rd(item.total || 0)}</div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${item.status === "error" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                      {item.status === "error" ? "Error" : "Pendiente"}
                    </span>
                  </div>
                  {item.status === "error" && (
                    <div className="mt-2 text-xs text-red-600 flex items-start gap-1.5"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {item.error}</div>
                  )}
                  <div className="flex gap-2 mt-2.5">
                    <button onClick={() => reprint(item)} className="inline-flex items-center gap-1 text-xs border border-neutral-200 rounded-full px-3 py-1.5 hover:border-espresso"><Printer size={12} /> Reimprimir</button>
                    {item.status === "error" && (
                      <button onClick={() => retry(item)} className="inline-flex items-center gap-1 text-xs border border-neutral-200 rounded-full px-3 py-1.5 hover:border-espresso"><RefreshCw size={12} /> Reintentar</button>
                    )}
                    <button onClick={() => discard(item)} className="inline-flex items-center gap-1 text-xs border border-neutral-200 rounded-full px-3 py-1.5 text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={12} /> Descartar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const round2 = (n) => Math.round(n * 100) / 100;
